import { useEffect, useMemo } from "react";
import { Layers, Loader2, Plus } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/modern/components/ui/resizable";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import {
  CONTEXT_GUIDE_TARGETS,
  guideTargetProps,
  useAutoPageGuide,
} from "@/react/guides";
import {
  MarketingStackWorkflowStrip,
  marketingStackCardClassName,
  marketingStackContentInsetClassName,
  marketingStackPageClassName,
} from "@/react/components/marketingStack";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { OPENAI_KEY_REQUIRED_TITLE, useHasOpenAIKey } from "@/react/hooks/useHasOpenAIKey";
import { IntegrationAlert, useOpenAIKeyModal } from "@/integrations";
import { useBoardPermissions } from "../settings/hooks/useBoardPermissions";
import { ContextPageProvider, useContextPage } from "./context/ContextPageContext";
import { AsyncTaskHandlerProvider, useAsyncTaskHandler } from "./context/BoardContextTaskHandler";
import { ContextFormProvider } from "./context/ContextFormProvider";
import { ContextList } from "./components/ContextList";
import { ContextDetail } from "./components/ContextDetail";
import { AddContextModal } from "./components/AddContextModal";
import { useHasNonPreviewGroups } from "./context/useContextViewState";
import { contextDisplayName } from "./utils/contextDisplayName";

const NoSelectionState = () => (
  <div
    className="flex flex-col h-full items-center justify-center gap-4 px-8 text-center"
    {...guideTargetProps(CONTEXT_GUIDE_TARGETS.emptyState)}
  >
    <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-border bg-muted">
      <Layers className="h-6 w-6 text-muted-foreground" />
    </div>
    <div>
      <p className="text-base font-semibold text-foreground">Select a context</p>
      <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed max-w-[260px]">
        Choose a context from the list on the left, or create a new one to get started.
      </p>
    </div>
  </div>
);

const PageSkeleton = () => (
  <div className="flex h-full items-center justify-center">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

const OpenAIKeyBanner = () => {
  const openAiKeyModal = useOpenAIKeyModal();
  const hasOpenAIKey = useHasOpenAIKey();
  const { canWrite } = useBoardPermissions();

  if (hasOpenAIKey) return null;

  return (
    <IntegrationAlert
      className="shrink-0 mx-5 mt-4 mb-1"
      variant="missing_config"
      provider="openai"
      title={OPENAI_KEY_REQUIRED_TITLE}
      message={
        canWrite
          ? "An API key is needed to generate context and questions. Click the button to configure your API key."
          : "An API key is needed to generate context and questions. Only the Board Owner can configure the API key."
      }
      action={
        canWrite
          ? {
              label: "Configure Key",
              onClick: () => openAiKeyModal?.open(),
            }
          : undefined
      }
    />
  );
};

const ContextPageViewContent = () => {
  const { isReady } = useAsyncTaskHandler();
  const hasOpenAIKey = useHasOpenAIKey();
  const {
    contexts,
    panelContextId,
    modalOpen,
    modalContextId,
    modalMode,
    modalSessionKey,
    isCreatingNew,
    beginNewContext,
    commitNewWizardContext,
    cancelNewContext,
    hideModal,
    handoffModalToPanel,
    duplicateContextForExtend,
    finalizeExtendDuplicate,
    selectContext,
  } = useContextPage();
  const { viewManagerState } = useSwivuBryntumContext() as {
    viewManagerState?: { selectedView?: { otherParams?: { contextId?: number } } };
  };

  useEffect(() => {
    const contextId = viewManagerState?.selectedView?.otherParams?.contextId;
    if (contextId != null) {
      selectContext(Number(contextId));
    }
  }, [viewManagerState?.selectedView?.otherParams?.contextId, selectContext]);

  const panelContext = contexts.find((c) => c.id === panelContextId) ?? null;
  const contextLabel = panelContext?.name?.trim()
    ? contextDisplayName(panelContext.name)
    : null;
  const panelHasGroups = useHasNonPreviewGroups(panelContextId);
  const canRenderPanel = panelContext != null && (panelContext.status === "completed" || panelHasGroups);

  const guideEnabled = isReady && !modalOpen;

  const { replayGuide } = useAutoPageGuide({
    viewType: VIEW_TYPES.context,
    enabled: guideEnabled,
    contextKey: canRenderPanel ? "detail" : "empty",
  });

  if (!isReady) return <PageSkeleton />;

  const rightPanel = () => {
    if (canRenderPanel && panelContext) {
      return (
        <ContextFormProvider
          key={`panel-${panelContext.id}`}
          contextId={panelContext.id}
          mode="resume"
          panelMode
        >
          <ContextDetail
            contextRecord={panelContext}
            showWizardBanner={true}
          />
        </ContextFormProvider>
      );
    }
    return <NoSelectionState />;
  };

  return (
    <div className={marketingStackPageClassName}>
      <MarketingStackWorkflowStrip
        activeView={VIEW_TYPES.context}
        contextLabel={contextLabel}
        stepOverrides={{
          [VIEW_TYPES.context]: {
            props: guideTargetProps(CONTEXT_GUIDE_TARGETS.workflowContext),
            onShowGuide: guideEnabled ? replayGuide : undefined,
          },
          [VIEW_TYPES.marketingManifest]: {
            props: guideTargetProps(CONTEXT_GUIDE_TARGETS.manifestLink),
          },
        }}
        trailing={
          <Button
            size="sm"
            onClick={beginNewContext}
            disabled={isCreatingNew || !hasOpenAIKey}
            title={!hasOpenAIKey ? OPENAI_KEY_REQUIRED_TITLE : undefined}
            {...guideTargetProps(CONTEXT_GUIDE_TARGETS.newContext)}
            className="h-8 shrink-0 gap-1.5 px-3.5 text-xs font-semibold shadow-md"
          >
            <Plus className="h-3.5 w-3.5" />
            New Context
          </Button>
        }
      />
      <div className={marketingStackContentInsetClassName}>
        <ResizablePanelGroup
          orientation="horizontal"
          className={marketingStackCardClassName()}
        >
          <ResizablePanel
            id="context-list"
            defaultSize={"20%"}
            minSize={"180px"}
            maxSize={"30%"}
            className="border-r border-border"
          >
            <ContextList />
          </ResizablePanel>

          <ResizableHandle withHandle />

          <ResizablePanel id="context-detail" defaultSize={"80%"} minSize={"60%"}>
            <div className="flex flex-col h-full min-h-0 overflow-hidden">
              <OpenAIKeyBanner />
              <div className="flex-1 min-h-0 overflow-hidden">{rightPanel()}</div>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {modalOpen && modalSessionKey && (
        <ContextFormProvider
          key={modalSessionKey}
          contextId={modalContextId}
          draftNewContext={modalMode === "create" && isCreatingNew}
          mode={modalMode ?? "resume"}
          duplicateContextForExtend={duplicateContextForExtend}
          finalizeExtendDuplicate={finalizeExtendDuplicate}
          onContextCreated={commitNewWizardContext}
          onDraftCancel={modalMode === "create" ? cancelNewContext : undefined}
        >
          <AddContextModal
            open={modalOpen}
            mode={modalMode ?? "resume"}
            contextId={modalContextId}
            onHide={hideModal}
            onHandoff={handoffModalToPanel}
          />
        </ContextFormProvider>
      )}
    </div>
  );
};

const ContextPageView = () => {
  const { selectedBoard } = useSwivuBryntumContext();
  const boardId = useMemo(() => selectedBoard?.id, [selectedBoard]);

  return (
    <AsyncTaskHandlerProvider boardId={boardId}>
      <ContextPageProvider>
        <ContextPageViewContent />
      </ContextPageProvider>
    </AsyncTaskHandlerProvider>
  );
};

export default ContextPageView;
